import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  getDocs, 
  query, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, deleteImage, generateImageFilename } from './imageService';
import { 
  getEffectiveAccountId, 
  validateResourceAccess, 
  logActivity,
  hasPermission 
} from './authorizationService';
import { AUDIT_ACTIONS, RESOURCE_TYPES, PERMISSIONS } from '../types/rbac';

const COLLECTION_NAME = 'socialMediaPosts';

// Create a new social media post
export const createSocialMediaPost = async (postData) => {
  try {
    // Check permissions
    if (!hasPermission(PERMISSIONS.CREATE_POSTS)) {
      throw new Error('Insufficient permissions to create posts');
    }

    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    let imageUrl = '';
    
    // Handle image upload if present
    if (postData.image && postData.image instanceof File) {
      // First create the post to get an ID
      const tempPost = {
        ...postData,
        image: '', // Temporarily empty
        accountId, // Add tenant scoping
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), tempPost);
      const postId = docRef.id;
      
      // Generate filename and upload image
      const filename = generateImageFilename(postData.image.name, postId);
      imageUrl = await uploadImage(postData.image, filename);
      
      // Update the post with the image URL
      const finalPost = {
        ...postData,
        image: imageUrl,
        accountId, // Add tenant scoping
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(docRef, finalPost);
      
      // Log activity
      await logActivity(AUDIT_ACTIONS.POST_CREATED, RESOURCE_TYPES.POST, postId, {
        platform: postData.platform,
        hasImage: !!imageUrl
      });
      
      return { id: postId, ...finalPost };
    } else {
      // No image to upload
      const postWithTimestamp = {
        ...postData,
        accountId, // Add tenant scoping
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), postWithTimestamp);
      
      // Log activity
      await logActivity(AUDIT_ACTIONS.POST_CREATED, RESOURCE_TYPES.POST, docRef.id, {
        platform: postData.platform,
        hasImage: false
      });
      
      return { id: docRef.id, ...postWithTimestamp };
    }
    } catch (error) {
    console.error('Error creating social media post:', error);
    throw error;
  }
};

// Get all social media posts
export const getSocialMediaPosts = async () => {
  try {
    // Check permissions
    if (!hasPermission(PERMISSIONS.VIEW_POSTS)) {
      throw new Error('Insufficient permissions to view posts');
    }

    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const q = query(
      collection(db, COLLECTION_NAME),
      where('accountId', '==', accountId) // Filter by tenant only
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      const postData = doc.data();
      // Double-check tenant access
      if (validateResourceAccess(postData)) {
        posts.push({
          id: doc.id,
          ...postData
        });
      }
    });
    
    // Sort by createdAt in JavaScript to avoid Firestore index requirement
    posts.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return bTime - aTime; // Descending order (newest first)
    });
    
    return posts;
    } catch (error) {
    console.error('Error getting social media posts:', error);
    throw error;
  }
};

// Update a social media post
export const updateSocialMediaPost = async (postId, updateData) => {
  try {
    // Check permissions
    if (!hasPermission(PERMISSIONS.EDIT_POSTS)) {
      throw new Error('Insufficient permissions to edit posts');
    }

    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const postRef = doc(db, COLLECTION_NAME, postId);
    
    // First, get the existing post to validate access
    const postDoc = await getDoc(postRef);
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const existingPost = postDoc.data();
    if (!validateResourceAccess(existingPost)) {
      throw new Error('Access denied: Post does not belong to your account');
    }
    
    let finalUpdateData = { ...updateData };
    
    // Handle image upload if present
    if (updateData.image && updateData.image instanceof File) {
      // Generate filename and upload new image
      const filename = generateImageFilename(updateData.image.name, postId);
      const imageUrl = await uploadImage(updateData.image, filename);
      finalUpdateData.image = imageUrl;
    }
    
    const updateWithTimestamp = {
      ...finalUpdateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(postRef, updateWithTimestamp);
    
    // Log activity
    await logActivity(AUDIT_ACTIONS.POST_UPDATED, RESOURCE_TYPES.POST, postId, {
      platform: updateData.platform || existingPost.platform,
      hasImage: !!finalUpdateData.image
    });
    
    return { id: postId, ...finalUpdateData };
  } catch (error) {
    console.error('Error updating social media post:', error);
    throw error;
  }
};

// Delete a social media post
export const deleteSocialMediaPost = async (postId, imageUrl = null) => {
  try {
    // Check permissions
    if (!hasPermission(PERMISSIONS.DELETE_POSTS)) {
      throw new Error('Insufficient permissions to delete posts');
    }

    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const postRef = doc(db, COLLECTION_NAME, postId);
    
    // First, get the existing post to validate access
    const postDoc = await getDoc(postRef);
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const existingPost = postDoc.data();
    if (!validateResourceAccess(existingPost)) {
      throw new Error('Access denied: Post does not belong to your account');
    }
    
    // Delete the image from storage if it exists
    if (imageUrl) {
      await deleteImage(imageUrl);
    }
    
    // Delete the post document
    await deleteDoc(postRef);
    
    // Log activity
    await logActivity(AUDIT_ACTIONS.POST_DELETED, RESOURCE_TYPES.POST, postId, {
      platform: existingPost.platform,
      hadImage: !!imageUrl
    });
    
    return postId;
    } catch (error) {
    console.error('Error deleting social media post:', error);
    throw error;
  }
};

// Get posts by status
export const getSocialMediaPostsByStatus = async (status) => {
  try {
    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const q = query(
      collection(db, COLLECTION_NAME),
      where('accountId', '==', accountId),
      where('status', '==', status)
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      const postData = doc.data();
      if (validateResourceAccess(postData)) {
        posts.push({
          id: doc.id,
          ...postData
        });
      }
    });
    
    // Sort by createdAt in JavaScript
    posts.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return bTime - aTime; // Descending order (newest first)
    });
    
    return posts;
    } catch (error) {
    console.error('Error getting social media posts by status:', error);
    throw error;
  }
};

// Get posts by platform
export const getSocialMediaPostsByPlatform = async (platform) => {
  try {
    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const q = query(
      collection(db, COLLECTION_NAME),
      where('accountId', '==', accountId),
      where('platform', '==', platform)
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      const postData = doc.data();
      if (validateResourceAccess(postData)) {
        posts.push({
          id: doc.id,
          ...postData
        });
      }
    });
    
    // Sort by createdAt in JavaScript
    posts.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return bTime - aTime; // Descending order (newest first)
    });
    
    return posts;
  } catch (error) {
    console.error('Error getting social media posts by platform:', error);
    throw error;
  }
};

// Get upcoming posts (scheduled for future)
export const getUpcomingSocialMediaPosts = async () => {
  try {
    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const now = new Date();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('accountId', '==', accountId),
      where('date', '>=', now.toISOString())
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      const postData = doc.data();
      if (validateResourceAccess(postData)) {
        posts.push({
          id: doc.id,
          ...postData
        });
      }
    });
    
    // Sort by date in JavaScript (ascending for upcoming posts)
    posts.sort((a, b) => {
      const aTime = new Date(a.date);
      const bTime = new Date(b.date);
      return aTime - bTime; // Ascending order (earliest first)
    });
    
    return posts;
    } catch (error) {
    console.error('Error getting upcoming social media posts:', error);
    throw error;
  }
};

// Get past posts (published or scheduled in the past)
export const getPastSocialMediaPosts = async () => {
  try {
    // Get effective account ID for tenant scoping
    const accountId = getEffectiveAccountId();
    if (!accountId) {
      throw new Error('No account scope set. Please select an account.');
    }

    const now = new Date();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('accountId', '==', accountId),
      where('date', '<', now.toISOString())
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      const postData = doc.data();
      if (validateResourceAccess(postData)) {
        posts.push({
          id: doc.id,
          ...postData
        });
      }
    });
    
    // Sort by date in JavaScript (descending for past posts)
    posts.sort((a, b) => {
      const aTime = new Date(a.date);
      const bTime = new Date(b.date);
      return bTime - aTime; // Descending order (most recent first)
    });
    
    return posts;
  } catch (error) {
    console.error('Error getting past social media posts:', error);
    throw error;
  }
};